/**
 * AppointmentLinkedItemsPanel — Feb 2026
 *
 * Mounted inside the appointment-edit dialog (and reachable from the
 * calendar) to surface every Task and Note tied to a given visit.
 *
 * Why a separate component:
 *   - Keeps the multi-thousand-line appointment dialog in App.js
 *     focused on the form itself.
 *   - Self-contained data-fetch + cache via GET
 *     /api/appointments/:id/linked-items.
 *   - Lets us reuse the same panel from a calendar pop-over if/when
 *     we add one — same props.
 *
 * Empty-state behaviour:
 *   - When the appointment has no linked tasks AND no linked notes,
 *     render a one-line "no follow-ups yet" message instead of
 *     swallowing dialog space.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_STYLE = {
  urgent: 'bg-red-100 text-red-800 border-red-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  normal: 'bg-slate-100 text-slate-700 border-slate-300',
  low: 'bg-slate-50 text-slate-500 border-slate-200',
};

const STATUS_STYLE = {
  pending: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-emerald-100 text-emerald-800',
  dismissed: 'bg-slate-100 text-slate-500 line-through',
};

export const AppointmentLinkedItemsPanel = ({ appointmentId, onOpenNote }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!appointmentId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/appointments/${appointmentId}/linked-items`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ tasks: [], notes: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [appointmentId]);

  if (!appointmentId) return null;
  if (loading && !data) {
    return (
      <div className="border-t mt-3 pt-3 text-xs text-slate-500">Loading follow-ups…</div>
    );
  }
  if (!data) return null;

  const tasks = data.tasks || [];
  const notes = data.notes || [];

  if (tasks.length === 0 && notes.length === 0) {
    return (
      <div
        className="border-t mt-3 pt-3 text-xs text-slate-500"
        data-testid="appointment-linked-items-empty"
      >
        No follow-up tasks or notes attached to this appointment yet.
      </div>
    );
  }

  return (
    <div className="border-t mt-3 pt-3 space-y-3" data-testid="appointment-linked-items-panel">
      {tasks.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-1">
            Linked Tasks ({tasks.length})
          </div>
          <div className="space-y-1.5">
            {tasks.map((t) => {
              // Mar 2026 — When a task was spawned from a provider's
              // note (e.g. "reschedule in 4 weeks"), surface the
              // provider's instruction prominently and label the due
              // date as "Follow up by" so the scheduler doesn't
              // misread it as the target appointment date.
              const isFromNote = !!t.source_note_id;
              // Heuristic: if the description is a reschedule
              // instruction ("reschedule in 4 weeks", "rebook for X"),
              // we already render the body — no extra parsing required.
              return (
              <div
                key={t.id}
                className="border rounded p-2 text-xs flex items-start gap-2 bg-white"
                data-testid={`appointment-linked-task-${t.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{t.title}</div>
                  {isFromNote && t.description && (
                    <div
                      className="mt-1 p-1.5 rounded border border-amber-300 bg-amber-50"
                      data-testid={`appointment-linked-task-provider-note-${t.id}`}
                    >
                      <div className="text-[9px] uppercase tracking-wide font-semibold text-amber-800 mb-0.5">
                        Note from {t.assigned_by_name || 'provider'}
                      </div>
                      <p className="text-[11px] text-amber-900 whitespace-pre-wrap break-words">
                        {t.description}
                      </p>
                    </div>
                  )}
                  {/* Mar 2026 — When the create_task widget's own
                      description is empty (common: providers type
                      their reschedule instructions into the note's
                      Details / Plan / Reschedule field instead of
                      the widget's description box), surface the
                      relevant note content here so the scheduler
                      doesn't have to open the source note to see
                      "Rebook in 8 weeks". */}
                  {isFromNote && !t.description && Array.isArray(t.source_note_snippet) && t.source_note_snippet.length > 0 && (t.action_type || '').toLowerCase() !== 'reschedule_appointment' && (
                    <div
                      className="mt-1 p-1.5 rounded border border-amber-300 bg-amber-50 space-y-1"
                      data-testid={`appointment-linked-task-source-note-snippet-${t.id}`}
                    >
                      <div className="text-[9px] uppercase tracking-wide font-semibold text-amber-800">
                        From {t.assigned_by_name || 'the source note'}
                      </div>
                      {t.source_note_snippet.map((entry, idx) => (
                        <div key={idx}>
                          <div className="text-[9px] font-semibold text-amber-900 uppercase tracking-wide">
                            {entry.label}
                          </div>
                          <p className="text-[11px] text-amber-900 whitespace-pre-wrap break-words">
                            {entry.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${STATUS_STYLE[t.status] || STATUS_STYLE.pending}`}>
                      {t.status?.replace(/_/g, ' ') || 'pending'}
                    </span>
                    {t.priority && (
                      <span className={`px-1.5 py-0.5 rounded border text-[10px] ${PRIORITY_STYLE[t.priority] || PRIORITY_STYLE.normal}`}>
                        {t.priority}
                      </span>
                    )}
                    {t.due_date && (
                      <span
                        className="text-[10px] text-slate-600"
                        title={isFromNote
                          ? "When the scheduler should action this task — NOT the appointment target date. See the provider's note above."
                          : undefined}
                      >
                        {isFromNote ? 'Follow up by' : 'Due'} {t.due_date}
                      </span>
                    )}
                    {t.assigned_to_name && (
                      <span className="text-[10px] text-slate-600">
                        → {t.assigned_to_name}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('openTaskDrawer', {
                        detail: { task_id: t.id },
                      }));
                    }}
                    data-testid={`appointment-linked-task-open-${t.id}`}
                  >
                    Open
                  </Button>
                  {t.source_note_id && onOpenNote && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-[10px] text-slate-500"
                      onClick={() => onOpenNote(t.source_note_id)}
                    >
                      Source note
                    </Button>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {notes.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-slate-700 mb-1">
            Notes ({notes.length})
          </div>
          <div className="space-y-1.5">
            {notes.map((n) => (
              <div
                key={n.id}
                className="border rounded p-2 text-xs flex items-center gap-2 bg-white"
                data-testid={`appointment-linked-note-${n.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">
                    {n.template_name || n.form_type || 'Note'}
                  </div>
                  <div className="text-[10px] text-slate-600 mt-0.5">
                    {n.status?.replace(/_/g, ' ') || 'draft'}
                    {n.date_of_service && ` · DOS ${n.date_of_service}`}
                    {n.provider_name && ` · ${n.provider_name}`}
                  </div>
                </div>
                {onOpenNote && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={() => onOpenNote(n.id)}
                    data-testid={`appointment-linked-note-open-${n.id}`}
                  >
                    Open
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentLinkedItemsPanel;
